/**
 * Browser helpers for Voice/Video: unlock autoplay, request the mic on the
 * Join tap, keep one Jitsi identity per person, and parse the embed URL.
 */

export const SESSION_MEDIA_LOCK_PREFIX = 'sl-session-media-lock:';
export const SESSION_MEDIA_OWNER_PREFIX = 'sl-session-media-owner:';
export const SESSION_MEDIA_ENTERED_PREFIX = 'sl-session-entered:';
export const SESSION_MEDIA_SOFT_LEFT_PREFIX = 'sl-session-soft-left:';
export const SESSION_MEDIA_CLOCK_PREFIX = 'sl-session-clock:';
export const SESSION_MEDIA_CONNECTION_PREFIX = 'sl-session-conn:';
// iOS Safari permission sheets often sit longer than 4s. Hang up only after
// the tab stays hidden well past Allow / Don't Allow.
// Oct 28: brief app switches (texts/email) must not hang up or mark left.
export const PAGEHIDE_HANGUP_GRACE_MS = 300000; // Sam Nov 1: tab-away must not hang up (was 75s)
export const CONFERENCE_LEFT_JOIN_GRACE_MS = 25000;
export const SESSION_MEDIA_LOCK_STALE_MS = 120000;

const audioOutputLabel = device =>
  typeof device?.label === 'string' && device.label.trim() ? device.label.trim() : 'Speaker';

/**
 * True on iPhone/iPad Safari (and iOS Chrome, which is still WebKit).
 *
 * @param {string} [userAgent]
 * @returns {boolean}
 */
export const isIOSWebKit = (
  userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : ''
) =>
  /iP(hone|od|ad)/.test(userAgent) ||
  (/Macintosh/.test(userAgent) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1);

/**
 * Parse host, room, and display name from a Jitsi embed URL.
 *
 * @param {string} url
 * @returns {{ host: string, roomName: string, displayName: string }|null}
 */
export const parseJitsiEmbedUrl = url => {
  if (!url || typeof url !== 'string' || url.indexOf('http') !== 0) {
    return null;
  }
  try {
    const parsed = new URL(url);
    const roomName = decodeURIComponent(parsed.pathname.replace(/^\/+/, '').split('/')[0] || '');
    if (!parsed.hostname || !roomName) {
      return null;
    }
    const hash = decodeURIComponent(parsed.hash.replace(/^#/, ''));
    const nameMatch =
      hash.match(/userInfo\.displayName="([^"]*)"/) || hash.match(/userInfo\.displayName=([^&]+)/);
    return {
      host: parsed.hostname,
      roomName,
      displayName: nameMatch ? decodeURIComponent(nameMatch[1].replace(/^"+|"+$/g, '')) : '',
    };
  } catch (e) {
    return null;
  }
};

/**
 * Stable iframe allow list. Wildcards delegate mic/autoplay into Jitsi.
 *
 * @param {'voice'|'video'} [mode]
 * @returns {string}
 */
export const sessionIframeAllow = (mode = 'video') =>
  mode === 'voice'
    ? 'microphone *; autoplay *; fullscreen *; clipboard-write *'
    : 'camera *; microphone *; autoplay *; fullscreen *; display-capture *; clipboard-write *';

const SESSION_UNLOCK_SRC = '/static/session-unlock.wav';

const playSessionUnlockFile = (volume = 0.01) => {
  if (typeof Audio === 'undefined') {
    return Promise.resolve(false);
  }
  try {
    const audio = new Audio(SESSION_UNLOCK_SRC);
    audio.setAttribute('playsinline', 'true');
    audio.volume = volume;
    return audio
      .play()
      .then(() => true)
      .catch(() => false);
  } catch (e) {
    return Promise.resolve(false);
  }
};

const playSilentClick = () => playSessionUnlockFile(0.01);

/**
 * Audible ping for a Buzz / Join popup. Same file as the unlock click;
 * iOS may still block it without a prior gesture.
 *
 * @returns {Promise<boolean>}
 */
export const playSessionBuzzAlert = () => playSessionUnlockFile(0.7);

/**
 * Unlock output on the Join tap. Safari often blocks remote audio when the
 * iframe mounts after an async join call.
 *
 * @returns {Promise<boolean>}
 */
export const unlockSessionOutput = async () => {
  if (typeof window === 'undefined') {
    return false;
  }
  let resumed = false;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (Ctx) {
    try {
      const ctx = new Ctx();
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      const buffer = ctx.createBuffer(1, 1, 22050);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
      resumed = ctx.state === 'running';
    } catch (e) {
      resumed = false;
    }
  }
  try {
    const clicked = await playSilentClick();
    return resumed || clicked;
  } catch (e) {
    return resumed;
  }
};

/**
 * Ask for the microphone (and camera on Video) during the Join user gesture.
 * Tracks are stopped so Jitsi can take the device.
 *
 * @param {{ video?: boolean }} [options]
 * @returns {Promise<{ ok: boolean, code: string|null }>}
 */
export const requestSessionMicrophone = async ({ video = false } = {}) => {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return { ok: false, code: 'unsupported' };
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
      video: video ? { facingMode: 'user' } : false,
    });
    stream.getTracks().forEach(track => track.stop());
    return { ok: true, code: null };
  } catch (error) {
    return { ok: false, code: error?.name || 'denied' };
  }
};

/**
 * Unlock speakers and request the mic on the Join tap.
 *
 * @param {{ video?: boolean }} [options]
 * @returns {Promise<{ unlocked: boolean, mic: { ok: boolean, code: string|null } }>}
 */
const withTimeout = (promise, ms, fallback) =>
  Promise.race([
    promise,
    new Promise(resolve => {
      window.setTimeout(() => resolve(fallback), ms);
    }),
  ]);

/**
 * Re-bind remote MediaStream tracks after a Rejoin. iOS Safari often keeps
 * the element paused and the track enabled=false until srcObject is reset
 * during a user gesture.
 *
 * @param {HTMLMediaElement} el
 * @returns {number} tracks re-attached
 */
export const reattachRemoteAudioElement = el => {
  if (!el) {
    return 0;
  }
  try {
    el.muted = false;
    el.defaultMuted = false;
    if (typeof el.volume === 'number') {
      el.volume = 1;
    }
    el.setAttribute('playsinline', 'true');
    el.playsInline = true;
  } catch (e) {
    // Ignore read-only media properties.
  }
  const stream = el.srcObject;
  if (!stream || typeof stream.getAudioTracks !== 'function') {
    return 0;
  }
  const tracks = stream.getAudioTracks();
  tracks.forEach(track => {
    try {
      track.enabled = true;
    } catch (e) {
      // Ignore ended tracks.
    }
  });
  try {
    el.srcObject = null;
    el.srcObject = stream;
  } catch (e) {
    return 0;
  }
  return tracks.length;
};

/**
 * Call play() on every audio/video element under root. iOS Safari often
 * leaves remote Jitsi/WebRTC audio paused until a user gesture.
 *
 * @param {ParentNode|null} root
 * @returns {Promise<{ played: number, blocked: number, reattached: number }>}
 */
export const playSessionMediaElements = async (root = null) => {
  const scope =
    root && typeof root.querySelectorAll === 'function'
      ? root
      : typeof document !== 'undefined'
      ? document
      : null;
  if (!scope) {
    return { played: 0, blocked: 0, reattached: 0 };
  }
  const nodes = Array.from(scope.querySelectorAll('audio, video'));
  let reattached = 0;
  const results = await Promise.all(
    nodes.map(el => {
      reattached += reattachRemoteAudioElement(el);
      try {
        const playResult = typeof el.play === 'function' ? el.play() : null;
        if (playResult && typeof playResult.then === 'function') {
          return playResult.then(() => 'played').catch(() => 'blocked');
        }
        return 'played';
      } catch (e) {
        return 'blocked';
      }
    })
  );
  return {
    played: results.filter(row => row === 'played').length,
    blocked: results.filter(row => row === 'blocked').length,
    reattached,
  };
};

/**
 * Unlock output after a tap, then play parent (and same-origin iframe)
 * media. Cross-origin Jitsi cannot be reached; the tap still resumes
 * AudioContext so a later iframe gesture is more likely to work.
 *
 * @param {{ api?: { getIFrame?: Function }, iframe?: HTMLIFrameElement|null }} [options]
 * @returns {Promise<{ unlocked: boolean, played: number, blocked: number }>}
 */
export const enableRemoteAudioPlayback = async ({ api, iframe } = {}) => {
  const unlocked = await unlockSessionOutput();
  let played = await playSessionMediaElements(typeof document !== 'undefined' ? document : null);
  let frame = iframe || null;
  if (!frame && api && typeof api.getIFrame === 'function') {
    try {
      frame = api.getIFrame();
    } catch (e) {
      frame = null;
    }
  }
  if (frame) {
    try {
      const doc = frame.contentDocument || frame.contentWindow?.document;
      if (doc) {
        const inner = await playSessionMediaElements(doc);
        played = {
          played: played.played + inner.played,
          blocked: played.blocked + inner.blocked,
          reattached: (played.reattached || 0) + (inner.reattached || 0),
        };
      }
    } catch (e) {
      // Cross-origin Jitsi embed — parent cannot call play() on remote audio.
    }
  }
  if (api && typeof api.setAudioOutputDevice === 'function') {
    try {
      api.setAudioOutputDevice('Speaker');
    } catch (e) {
      // Device list may be empty until Jitsi finishes GUM.
    }
  }
  if (api && typeof api.executeCommand === 'function') {
    try {
      api.executeCommand('setAudioOnly', false);
    } catch (e) {
      // Voice rooms already use startAudioOnly.
    }
  }
  return {
    unlocked: !!unlocked,
    played: played.played,
    blocked: played.blocked,
    reattached: played.reattached || 0,
  };
};

export const prepareSessionAudio = async ({ video = false, requestMic = false } = {}) => {
  const fallback = { unlocked: false, mic: { ok: false, code: 'unavailable' } };
  if (typeof window === 'undefined') {
    return fallback;
  }
  try {
    const unlocked = await withTimeout(unlockSessionOutput(), 800, false);
    if (!requestMic) {
      // Parent getUserMedia steals the only mic seat from Jitsi (silent
      // room both ways). Unlock speakers here; Jitsi owns the mic.
      return { unlocked: !!unlocked, mic: { ok: true, code: 'jitsi' } };
    }
    const mic = await withTimeout(requestSessionMicrophone({ video }), 4000, {
      ok: false,
      code: 'timeout',
    });
    return { unlocked: !!unlocked, mic };
  } catch (e) {
    return fallback;
  }
};

export const sessionMediaLockKey = (roomName, participantId) =>
  `${SESSION_MEDIA_LOCK_PREFIX}${roomName || 'room'}:${participantId || 'guest'}`;

const readLock = (roomName, participantId) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(sessionMediaLockKey(roomName, participantId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed?.ownerId || !parsed?.at) {
      return null;
    }
    if (Date.now() - parsed.at > SESSION_MEDIA_LOCK_STALE_MS) {
      return null;
    }
    return parsed;
  } catch (e) {
    return null;
  }
};

const writeLock = (roomName, participantId, ownerId) => {
  window.localStorage.setItem(
    sessionMediaLockKey(roomName, participantId),
    JSON.stringify({ ownerId, at: Date.now() })
  );
};

/**
 * One live Jitsi peer per user. A later Rejoin always takes the seat so a
 * stale lock or ghost tab cannot block the paid participant.
 *
 * @param {string} roomName
 * @param {string} participantId
 * @param {string} ownerId
 * @returns {{ ok: boolean, ownerId: string, takeover: boolean }}
 */
export const claimSessionMediaLock = (roomName, participantId, ownerId) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { ok: true, ownerId, takeover: false };
  }
  const existing = readLock(roomName, participantId);
  const takeover = !!(existing && existing.ownerId && existing.ownerId !== ownerId);
  writeLock(roomName, participantId, ownerId);
  return { ok: true, ownerId, takeover };
};

export const sessionMediaLockOwner = (roomName, participantId) =>
  readLock(roomName, participantId)?.ownerId || null;

export const heartbeatSessionMediaLock = (roomName, participantId, ownerId) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  const existing = readLock(roomName, participantId);
  if (existing && existing.ownerId !== ownerId) {
    return;
  }
  writeLock(roomName, participantId, ownerId);
};

export const releaseSessionMediaLock = (roomName, participantId, ownerId) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    const existing = readLock(roomName, participantId);
    if (existing && existing.ownerId !== ownerId) {
      return;
    }
    window.localStorage.removeItem(sessionMediaLockKey(roomName, participantId));
  } catch (e) {
    // Ignore quota / private-mode failures.
  }
};

export const newSessionMediaOwnerId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `tab-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;
};

/**
 * Same browser tab keeps the same owner id across remounts so a re-render
 * does not look like a second Maya.
 *
 * @param {string} roomName
 * @param {string} participantId
 * @returns {string}
 */
export const stableSessionMediaOwnerId = (roomName, participantId) => {
  const storageKey = `${SESSION_MEDIA_OWNER_PREFIX}${roomName || 'room'}:${participantId ||
    'guest'}`;
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return newSessionMediaOwnerId();
  }
  try {
    const existing = window.sessionStorage.getItem(storageKey);
    if (existing) {
      return existing;
    }
    const created = newSessionMediaOwnerId();
    window.sessionStorage.setItem(storageKey, created);
    return created;
  } catch (e) {
    return newSessionMediaOwnerId();
  }
};

export const sessionMediaChannelName = (roomName, participantId) =>
  `sl-session-media:${roomName || 'room'}:${participantId || 'guest'}`;

/**
 * True when another same-origin tab already holds this user's call.
 *
 * @param {string} roomName
 * @param {string} participantId
 * @param {string} ownerId
 * @returns {Promise<boolean>}
 */
export const askSessionMediaLive = (roomName, participantId, ownerId) => {
  if (typeof BroadcastChannel === 'undefined') {
    return Promise.resolve(false);
  }
  let channel;
  try {
    channel = new BroadcastChannel(sessionMediaChannelName(roomName, participantId));
  } catch (e) {
    return Promise.resolve(false);
  }
  return new Promise(resolve => {
    let done = false;
    const finish = live => {
      if (done) {
        return;
      }
      done = true;
      try {
        channel.close();
      } catch (e) {
        // Ignore.
      }
      resolve(live);
    };
    channel.onmessage = event => {
      const data = event?.data;
      if (data?.type === 'pong' && data.ownerId && data.ownerId !== ownerId) {
        finish(true);
      }
    };
    channel.postMessage({ type: 'ping', ownerId });
    setTimeout(() => finish(false), 200);
  });
};

/**
 * Answer pings from other tabs and hang up when a later Rejoin takes the seat.
 *
 * @param {string} roomName
 * @param {string} participantId
 * @param {string} ownerId
 * @param {(data: Object) => void} [onForeignMessage]
 * @returns {() => void}
 */
export const listenSessionMediaChannel = (roomName, participantId, ownerId, onForeignMessage) => {
  if (typeof BroadcastChannel === 'undefined') {
    return () => {};
  }
  let channel;
  try {
    channel = new BroadcastChannel(sessionMediaChannelName(roomName, participantId));
  } catch (e) {
    return () => {};
  }
  channel.onmessage = event => {
    const data = event?.data;
    if (data?.type === 'ping' && data.ownerId !== ownerId) {
      channel.postMessage({ type: 'pong', ownerId });
    }
    if (data?.type === 'takeover' && data.ownerId && data.ownerId !== ownerId) {
      if (typeof onForeignMessage === 'function') {
        onForeignMessage(data);
      }
    }
  };
  return () => {
    try {
      channel.close();
    } catch (e) {
      // Ignore.
    }
  };
};

export const postSessionMediaTakeover = (roomName, participantId, ownerId) => {
  if (typeof BroadcastChannel === 'undefined') {
    return;
  }
  let channel;
  try {
    channel = new BroadcastChannel(sessionMediaChannelName(roomName, participantId));
    channel.postMessage({ type: 'takeover', ownerId });
  } catch (e) {
    // Ignore missing BroadcastChannel.
  }
  if (channel) {
    try {
      channel.close();
    } catch (e) {
      // Ignore.
    }
  }
};

/**
 * Claim the single call seat. Last Rejoin wins so a ghost tab cannot lock out
 * the talker or listener on this booking.
 *
 * @param {string} roomName
 * @param {string} participantId
 * @param {string} ownerId
 * @returns {Promise<{ ok: boolean, ownerId: string, takeover: boolean }>}
 */
export const claimSessionMediaSeat = async (roomName, participantId, ownerId) => {
  const claimed = claimSessionMediaLock(roomName, participantId, ownerId);
  if (claimed.takeover) {
    postSessionMediaTakeover(roomName, participantId, ownerId);
  }
  return claimed;
};

export const sessionEnteredKey = transactionId =>
  `${SESSION_MEDIA_ENTERED_PREFIX}${transactionId || ''}`;

/**
 * Remember that this browser already entered the paid session so Rejoin
 * stays available after refresh or a closed tab.
 *
 * @param {string} transactionId
 */
export const rememberSessionEntered = transactionId => {
  if (typeof window === 'undefined' || !transactionId || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(sessionEnteredKey(transactionId), '1');
  } catch (e) {
    // Ignore quota / private mode.
  }
};

/**
 * @param {string} transactionId
 * @returns {boolean}
 */
export const hasRememberedSessionEntry = transactionId => {
  if (typeof window === 'undefined' || !transactionId || !window.localStorage) {
    return false;
  }
  try {
    return window.localStorage.getItem(sessionEnteredKey(transactionId)) === '1';
  } catch (e) {
    return false;
  }
};

export const sessionSoftLeftKey = transactionId =>
  `${SESSION_MEDIA_SOFT_LEFT_PREFIX}${transactionId || ''}`;

/**
 * Intentional End call. A permission-sheet pagehide must not set this,
 * or Rejoin after allow remounts as a fresh hang-up.
 *
 * @param {string} transactionId
 */
export const rememberSessionSoftLeft = transactionId => {
  if (typeof window === 'undefined' || !transactionId || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(sessionSoftLeftKey(transactionId), '1');
  } catch (e) {
    // Ignore quota / private mode.
  }
};

/**
 * @param {string} transactionId
 */
export const clearSessionSoftLeft = transactionId => {
  if (typeof window === 'undefined' || !transactionId || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.removeItem(sessionSoftLeftKey(transactionId));
  } catch (e) {
    // Ignore quota / private mode.
  }
};

/**
 * @param {string} transactionId
 * @returns {boolean}
 */
export const hasRememberedSessionSoftLeft = transactionId => {
  if (typeof window === 'undefined' || !transactionId || !window.localStorage) {
    return false;
  }
  try {
    return window.localStorage.getItem(sessionSoftLeftKey(transactionId)) === '1';
  } catch (e) {
    return false;
  }
};

/**
 * iOS Safari fires pagehide for the mic/camera sheet and bfcache.
 * Never hang up immediately — wait, and cancel if pageshow arrives.
 *
 * @param {PageTransitionEvent|{ persisted?: boolean }|null} [event]
 * @returns {boolean}
 */
export const shouldDeferPageHideHangup = (event = null) =>
  !event || event.persisted === true || event.persisted === undefined;

/**
 * First-connect and hidden-tab conference-left events are permission
 * flakes, not a real hang-up. Ignore until Jitsi has actually joined.
 *
 * @param {{ hidden?: boolean, msSinceJoin?: number, joinedOnce?: boolean }} [options]
 * @returns {boolean}
 */
export const shouldIgnoreConferenceLeft = ({
  hidden = false,
  msSinceJoin = 0,
  joinedOnce = true,
} = {}) =>
  joinedOnce === false ||
  hidden === true ||
  (Number(msSinceJoin) >= 0 && Number(msSinceJoin) < CONFERENCE_LEFT_JOIN_GRACE_MS);

/**
 * True when the tab is back in the foreground after a permission sheet
 * or bfcache hide. Cancel the deferred hang-up and remount if needed.
 *
 * @returns {boolean}
 */
export const isSessionPageForeground = () => {
  if (typeof document === 'undefined') {
    return true;
  }
  if (document.visibilityState) {
    return document.visibilityState === 'visible';
  }
  return document.hidden !== true;
};

export const sessionClockKey = transactionId =>
  `${SESSION_MEDIA_CLOCK_PREFIX}${transactionId || ''}`;

export const sessionConnectionKey = transactionId =>
  `${SESSION_MEDIA_CONNECTION_PREFIX}${transactionId || ''}`;

const newSessionConnectionId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `media-${Date.now()}`;

/**
 * Reuse the same media connection id across an iOS permission remount.
 * A fresh uuid looks like a ghost tab and the seat-kick closes Rejoin.
 *
 * @param {string} transactionId
 * @returns {string|null}
 */
export const readSessionConnectionId = transactionId => {
  if (typeof window === 'undefined' || !transactionId || !window.localStorage) {
    return null;
  }
  try {
    return window.localStorage.getItem(sessionConnectionKey(transactionId));
  } catch (e) {
    return null;
  }
};

/**
 * @param {string} transactionId
 * @param {string} connectionId
 */
export const rememberSessionConnectionId = (transactionId, connectionId) => {
  if (typeof window === 'undefined' || !transactionId || !connectionId || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(sessionConnectionKey(transactionId), connectionId);
  } catch (e) {
    // Ignore quota / private mode.
  }
};

/**
 * Stable id for this tab + booking. Remounts after Allow keep the seat.
 *
 * @param {string} transactionId
 * @returns {string}
 */
export const sessionConnectionIdFor = transactionId => {
  const existing = readSessionConnectionId(transactionId);
  if (existing) {
    return existing;
  }
  const next = newSessionConnectionId();
  rememberSessionConnectionId(transactionId, next);
  return next;
};

/**
 * Remember that both people joined so the site-wide banner can switch
 * from “starts when you’re both in” to “session in progress”.
 *
 * @param {string} transactionId
 */
export const rememberSessionClockLive = transactionId => {
  if (typeof window === 'undefined' || !transactionId || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(sessionClockKey(transactionId), '1');
  } catch (e) {
    // Ignore quota / private mode.
  }
};

/**
 * @param {string} transactionId
 * @returns {boolean}
 */
export const hasRememberedSessionClock = transactionId => {
  if (typeof window === 'undefined' || !transactionId || !window.localStorage) {
    return false;
  }
  try {
    return window.localStorage.getItem(sessionClockKey(transactionId)) === '1';
  } catch (e) {
    return false;
  }
};

/**
 * Prefer a device labeled speakerphone / speaker when the OS exposes one.
 *
 * @param {Array<{ deviceId?: string, label?: string }>} devices
 * @returns {{ deviceId: string, label: string }|null}
 */
export const pickSpeakerOutput = (devices = []) => {
  const usable = devices.filter(device => device?.deviceId);
  if (!usable.length) {
    return null;
  }
  const speaker = usable.find(device => /speaker|speakerphone|loud/i.test(device.label || ''));
  const chosen = speaker || usable[usable.length - 1];
  return { deviceId: chosen.deviceId, label: audioOutputLabel(chosen) };
};

const sessionCallWindows = new Map();

const windowLooksClosed = popup => {
  if (!popup) {
    return true;
  }
  try {
    return popup.closed === true;
  } catch (e) {
    return true;
  }
};

/**
 * Stable target so Leave / Rejoin / Open call reuse one browser window
 * instead of stacking `_blank` tabs.
 *
 * @param {string} [roomName]
 * @returns {string}
 */
export const sessionCallWindowName = (roomName = 'room') => {
  const safe = String(roomName || 'room').replace(/[^a-zA-Z0-9_-]/g, '');
  return `sl-session-call-${safe || 'room'}`;
};

/**
 * True when a desktop browser can keep Voice/Video in a separate window.
 * iPhone/iPad Safari treats a named window.open as this tab, so Join and
 * Rejoin stay in-page there.
 *
 * @param {string} [userAgent]
 * @returns {boolean}
 */
export const shouldOpenSessionCallInNewWindow = (
  userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : ''
) => !isIOSWebKit(userAgent);

/**
 * Open or focus the Voice/Video call window for this room.
 * Returns null on iOS and when Safari hijacks the current tab.
 *
 * @param {string} url
 * @param {string} [roomName]
 * @returns {Window|null}
 */
export const openSessionCallWindow = (url, roomName) => {
  if (!url || typeof window === 'undefined' || typeof window.open !== 'function') {
    return null;
  }
  if (!shouldOpenSessionCallInNewWindow()) {
    return null;
  }
  const name = sessionCallWindowName(roomName);
  const existing = sessionCallWindows.get(name);
  if (existing && existing !== window && !windowLooksClosed(existing)) {
    try {
      if (existing.location && existing.location.href !== url) {
        existing.location.replace(url);
      }
    } catch (e) {
      // Cross-origin Jitsi page — focusing is enough.
    }
    try {
      existing.focus();
    } catch (e) {
      // Ignore focus blocks.
    }
    return existing;
  }
  // Named target, no noopener — `_blank` + noopener always creates another window.
  const opened = window.open(url, name);
  // iOS / some desktop popup blockers reuse the current tab. Never treat
  // that as a successful handoff — the marketplace page would navigate away.
  if (!opened || opened === window) {
    return null;
  }
  sessionCallWindows.set(name, opened);
  try {
    opened.focus();
  } catch (e) {
    // Ignore focus blocks.
  }
  return opened;
};

/**
 * Close the named call window so Rejoin can remount the in-page room.
 *
 * @param {string} [roomName]
 */
export const closeSessionCallWindow = roomName => {
  const name = sessionCallWindowName(roomName);
  const existing = sessionCallWindows.get(name);
  sessionCallWindows.delete(name);
  if (!existing || windowLooksClosed(existing)) {
    return;
  }
  try {
    existing.close();
  } catch (e) {
    // Popup may already be gone.
  }
};

/**
 * Test helper.
 */
export const resetSessionCallWindows = () => {
  sessionCallWindows.forEach(popup => {
    try {
      if (popup && !popup.closed) {
        popup.close();
      }
    } catch (e) {
      // Ignore.
    }
  });
  sessionCallWindows.clear();
};
